import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type Cart,
  type Place,
  SessionApiError,
  addToCart,
  clearCart,
  getCart,
  newKey,
  resolvePostcode,
  setLineQuantity,
} from './store-api';

/** A loader for one read, keyed so a new key starts again. */
export type Loaded<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string; readonly notFound: boolean }
  | { readonly status: 'loaded'; readonly data: T };

export function useLoad<T>(key: string | undefined, load: () => Promise<T>): Loaded<T> & { reload(): void } {
  const [state, setState] = useState<Loaded<T>>({ status: 'loading' });
  const [round, setRound] = useState(0);
  const loader = useRef(load);
  loader.current = load;

  useEffect(() => {
    if (key === undefined) return undefined;
    let live = true;
    setState({ status: 'loading' });
    loader
      .current()
      .then((data) => {
        if (live) setState({ status: 'loaded', data });
      })
      .catch((error: unknown) => {
        if (!live) return;
        const status = error instanceof SessionApiError ? error.status : 0;
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : 'Something went wrong.',
          notFound: status === 404,
        });
      });
    return () => {
      live = false;
    };
  }, [key, round]);

  const reload = useCallback(() => setRound((value) => value + 1), []);
  return { ...state, reload };
}

/** Below 760px the frame turns the basket rail into a drawer. */
export function useNarrow(): boolean {
  const query = '(max-width: 759px)';
  const read = (): boolean => typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  const [narrow, setNarrow] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    const follow = (): void => setNarrow(list.matches);
    list.addEventListener?.('change', follow);
    return () => list.removeEventListener?.('change', follow);
  }, []);
  return narrow;
}

export function useToast(): readonly [string, (message: string) => void] {
  const [toast, setToast] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const flash = useCallback((message: string) => {
    if (timer.current !== undefined) clearTimeout(timer.current);
    setToast(message);
    timer.current = setTimeout(() => setToast(''), 2600);
  }, []);
  useEffect(
    () => () => {
      if (timer.current !== undefined) clearTimeout(timer.current);
    },
    [],
  );
  return [toast, flash] as const;
}

// -- Where the visitor is -------------------------------------------------------

const PLACE_KEY = 'jt-place';
const RECENT_KEY = 'jt-place-recent';

function readPlace(): Place | undefined {
  try {
    const raw = window.localStorage.getItem(PLACE_KEY);
    if (raw === null) return undefined;
    const parsed = JSON.parse(raw) as Place;
    return typeof parsed.postcode === 'string' ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function readRecent(): readonly Place[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(RECENT_KEY) ?? '[]') as Place[];
    return Array.isArray(parsed) ? parsed.filter((p) => typeof p.postcode === 'string').slice(0, 3) : [];
  } catch {
    return [];
  }
}

/**
 * The postcode the visitor chose, resolved by the platform's own location
 * route. A per-browser convenience, kept in storage and read defensively —
 * never sent anywhere but the routes that ask for a postcode.
 */
export function usePlace(): {
  readonly place: Place | undefined;
  readonly recent: readonly Place[];
  choose(postcode: string): Promise<Place>;
} {
  const [place, setPlace] = useState<Place | undefined>(readPlace);
  const [recent, setRecent] = useState<readonly Place[]>(readRecent);

  const choose = useCallback(async (postcode: string): Promise<Place> => {
    const found = await resolvePostcode(postcode.trim());
    const saved: Place = {
      postcode: found.postcode,
      ...(found.displayArea !== undefined ? { displayArea: found.displayArea } : {}),
      ...(found.coordinates !== undefined ? { coordinates: found.coordinates } : {}),
    };
    setPlace(saved);
    setRecent((previous) => {
      const next = [saved, ...previous.filter((p) => p.postcode !== saved.postcode)].slice(0, 3);
      try {
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch {
        // Not remembered; still used.
      }
      return next;
    });
    try {
      window.localStorage.setItem(PLACE_KEY, JSON.stringify(saved));
    } catch {
      // Not remembered; still used.
    }
    return saved;
  }, []);

  return { place, recent, choose };
}

// -- The basket -------------------------------------------------------------------

export interface CartRefusal {
  readonly message: string;
  /** The platform's refusal code: DIFFERENT_STOREFRONT, OUT_OF_STOCK, CONSULTATION_REQUIRED… */
  readonly code: string | undefined;
}

export function refusalOf(error: unknown): CartRefusal {
  if (error instanceof SessionApiError) {
    const details = error.details as { refusal?: unknown; code?: unknown };
    const code =
      typeof details.refusal === 'string'
        ? details.refusal
        : typeof details.code === 'string'
          ? details.code
          : error.code;
    return { message: error.message, code };
  }
  return { message: 'Something went wrong. Try again.', code: undefined };
}

/**
 * This browser's basket, as the platform holds it.
 *
 * Every change goes to the server and the page draws what comes back — the
 * page never adds up a basket of its own. One idempotency key per press.
 */
export function useCart(): {
  readonly cart: Cart | undefined;
  readonly busy: boolean;
  readonly failed: boolean;
  reload(): void;
  add(listingId: string, quantity: number, consultationId?: string): Promise<Cart>;
  setQuantity(lineId: string, quantity: number): Promise<Cart>;
  clear(): Promise<Cart>;
} {
  const [cart, setCart] = useState<Cart | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [round, setRound] = useState(0);

  useEffect(() => {
    let live = true;
    getCart()
      .then((found) => {
        if (!live) return;
        setCart(found);
        setFailed(false);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [round]);

  const run = useCallback(async (write: () => Promise<Cart>): Promise<Cart> => {
    setBusy(true);
    try {
      const next = await write();
      setCart(next);
      return next;
    } finally {
      setBusy(false);
    }
  }, []);

  return {
    cart,
    busy,
    failed,
    reload: () => setRound((value) => value + 1),
    add: (listingId, quantity, consultationId) =>
      run(() => addToCart(listingId, quantity, newKey(), consultationId)),
    setQuantity: (lineId, quantity) => run(() => setLineQuantity(lineId, quantity, newKey())),
    clear: () => run(() => clearCart(newKey())),
  };
}
